import { FaClock, FaRupeeSign, FaUsers, FaVideo, FaIndustry, FaCheckCircle, FaAward } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import { BsStarFill, BsLightningChargeFill } from 'react-icons/bs';

function HeroSection() {
  const stats = [
    { icon: <FaClock className="w-6 h-6" />, number: "25+", label: "Years Experience", sublabel: "in Payment Recovery", gradient: "from-orange-500 to-amber-500", bgGlow: "orange" },
    { icon: <FaRupeeSign className="w-6 h-6" />, number: "43Cr+", label: "Amount Recovered", sublabel: "Till Date", gradient: "from-emerald-500 to-green-500", bgGlow: "emerald" },
    { icon: <FaUsers className="w-6 h-6" />, number: "2,928+", label: "Total Students", sublabel: "in Community", gradient: "from-blue-500 to-cyan-500", bgGlow: "blue" },
    { icon: <FaVideo className="w-6 h-6" />, number: "76+", label: "Seminars & Webinars", sublabel: "Conducted", gradient: "from-purple-500 to-violet-500", bgGlow: "purple" },
    { icon: <FaIndustry className="w-6 h-6" />, number: "55+", label: "Industries Served", sublabel: "Till Date", gradient: "from-rose-500 to-pink-500", bgGlow: "rose" },
  ];

  return (
    <section className="relative pt-20 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        {/* Animated Gradient Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
          
          {/* Left Side - Image */}
          <div className="flex justify-center">
            <div className="relative">
              {/* Decorative Elements */}
              <div className="absolute -top-4 -left-4 w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl opacity-30 blur-xl"></div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl opacity-30 blur-xl"></div>
              
              {/* Main Image Container */}
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-3 border border-white/10 max-w-[320px]">
                {/* Floating Badge */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                  <FaAward />
                  <span>Expert Trainer</span>
                </div>
                
                {/* Image */}
                <div className="relative overflow-hidden rounded-2xl">
                  <img 
                    src="https://lp1.rushneeksingh.com/static/media/image.46016a24ba7916afc8d3.png" 
                    alt="Coach Rushneek Singh"
                    className="w-full h-auto rounded-2xl hover:scale-105 transition-transform duration-500"
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent"></div>
                  
                  {/* Name Badge on Image */}
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="bg-white/10 backdrop-blur-md rounded-xl p-2.5 border border-white/20">
                      <h3 className="text-white font-bold text-sm flex items-center gap-1.5">
                        Rushneek Singh
                        <FaCheckCircle className="text-blue-400 text-xs" />
                      </h3>
                      <p className="text-white/70 text-xs">B2B Payment Recovery Expert</p>
                    </div>
                  </div>
                </div>
                
                {/* Rating Badge */}
                <div className="absolute -right-3 top-1/4 bg-white/10 backdrop-blur-md border border-white/20 text-white px-3 py-2 rounded-xl shadow-xl">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <BsStarFill key={i} className="text-amber-400 text-xs" />
                    ))}
                  </div>
                  <div className="text-xs text-center mt-1 text-white/70">4.9/5</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Stats Cards */}
          <div className="lg:col-span-2">
            {/* Top Badge */}
            <div className="flex justify-center lg:justify-start mb-6">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-400 px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm">
                <HiSparkles className="text-amber-400" />
                <span>Proven Track Record</span>
                <BsLightningChargeFill className="text-amber-400" />
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {stats.map((stat, index) => (
                <div 
                  key={index}
                  className="group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 text-center hover:bg-white/10 hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden"
                >
                  {/* Hover Glow Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-15 transition-opacity duration-300`}></div>
                  
                  {/* Icon */}
                  <div className={`relative w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg text-white group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    {stat.icon}
                  </div>
                  
                  {/* Number */}
                  <h3 className={`relative text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${stat.gradient} mb-1`}>
                    {stat.number}
                  </h3>
                  
                  {/* Labels */}
                  <p className="relative text-white font-semibold text-xs sm:text-sm leading-tight">{stat.label}</p>
                  <p className="relative text-white/50 text-xs mt-0.5">{stat.sublabel}</p>
                  
                  {/* Bottom Accent Line */}
                  <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${stat.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}></div>
                </div>
              ))}
            </div>
            
            {/* Trust Text */}
            <div className="flex items-center justify-center lg:justify-start gap-4 mt-6 text-white/60 text-sm">
              <span className="flex items-center gap-1">
                <FaCheckCircle className="text-green-400" />
                Verified Results
              </span>
              <span className="w-1 h-1 bg-white/30 rounded-full"></span>
              <span className="flex items-center gap-1">
                <BsStarFill className="text-amber-400" />
                Trusted by 2,900+ Students
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
